import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Plus,
  Minus,
  Save,
  Trash2,
  Share2,
  MessageCircle,
  Copy,
  Printer,
  FileDown,
  Loader2,
  Check,
  X,
  Clock,
  User2,
  Package,
  Pencil,
  AlertTriangle,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import {
  CATS,
  DUR_OPTS,
  EVA_COLORS,
  EVA_TEXT,
  INC_QUESTIONS,
  INC_LABELS,
  CONDITIONS,
  EMOTIONS,
  NUTRITION_VIA,
  NUTRITION_TOLERANCE,
  NUTRITION_INTAKE,
  NUTRITION_APPETITE,
  NUTRITION_DIET,
  MEAL_PORTIONS,
  MEAL_TYPES,
  URINE_METHOD,
  URINE_APPEARANCE,
  URINE_VOLUME,
  STOOL_CONSISTENCY,
  STOOL_APPEARANCE,
  STOOL_METHOD,
  URINE_TIMES,
  STOOL_TIMES,
  BRISTOL_SCALE,
  MATERIAL_CATALOG,
  addMinutes,
} from "@/data/interventions";
import { computeShiftDuration, addMinutesToShift } from "@/lib/duration";
import { computeWaterBalance, BALANCE_STATUS } from "@/lib/balance";
import { evaluateVital } from "@/lib/vitals-range";
import {
  MORSE_QUESTIONS,
  computeMorseTotal,
  morseLevel,
  isMorseFilled,
} from "@/lib/morse";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { exportProgramPDF } from "@/lib/pdf";
import MedicationAdminLog from "@/components/MedicationAdminLog";
import EvidenceGallery from "@/components/EvidenceGallery";
import PeritonealDialysisSection from "@/components/PeritonealDialysisSection";
import GastrostomySection from "@/components/GastrostomySection";
import { indicationShort } from "@/lib/meds";
import {
  evaluateCritical,
  buildAlertMessage,
  openSupervisorWhatsApp,
  getSupervisorPhone,
  setSupervisorPhone,
  isValidPhone,
  normalizePhone,
} from "@/lib/critical-alert";
import { pendingRxDoses, formatDelay } from "@/lib/med-alerts";

// ----------------------------------------------------------------------------
// Section header
// ----------------------------------------------------------------------------
function SecHdr({ num, title }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#1D4ED8] text-sm font-bold text-white">
        {num}
      </div>
      <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
        {title}
      </h2>
      <div className="ml-1 h-px flex-1 bg-slate-200" />
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 ${className}`}>
      {children}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pills (radio group)
// ----------------------------------------------------------------------------
function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? "" : opt)}
            className={`min-h-[40px] rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
              active
                ? "border-[#1D4ED8] bg-[#1D4ED8] text-white shadow-sm"
                : "border-slate-200 bg-white text-slate-700 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
            }`}
            data-testid={`${testid}-opt-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// Section sub-label (small caps)
function SubLabel({ children }) {
  return (
    <div className="mb-1.5 mt-3 text-[11px] font-bold uppercase tracking-wide text-slate-500 first:mt-0">
      {children}
    </div>
  );
}

// Input + label for a single vital sign with automatic out-of-range styling and a
// tiny warning badge under the input.
function VitalField({ field, label, value, onChange, placeholder, type = "text", step, min, max, testid }) {
  const evalRes = evaluateVital(field, value);
  const abnormal = evalRes && evalRes.level !== "normal";
  const inputCls = abnormal
    ? "h-10 border-red-400 bg-red-50 text-red-900 text-sm font-bold focus-visible:ring-red-400"
    : "h-10 text-sm";
  return (
    <div>
      <Label className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </Label>
      <Input
        type={type}
        step={step}
        min={min}
        max={max}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        data-testid={testid}
      />
      {abnormal && (
        <div
          className="mt-1 text-[10px] font-bold uppercase tracking-wide text-red-600"
          data-testid={`${testid}-warning`}
        >
          {evalRes.level === "high" ? "▲" : "▼"} {evalRes.msg}
        </div>
      )}
    </div>
  );
}

// Inline form to add a custom material item (name + quantity + unit)
function CustomMaterialAdder({ onAdd }) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("pz");
  const submit = (e) => {
    e?.preventDefault?.();
    const n = name.trim();
    if (!n) return;
    onAdd(n, unit);
    setName("");
    setUnit("pz");
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3"
      data-testid="custom-material-form"
    >
      <div className="min-w-[160px] flex-1">
        <Label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
          Otro material
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. Bata desechable"
          className="h-10 text-sm"
          data-testid="custom-material-name"
        />
      </div>
      <div className="w-24">
        <Label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
          Unidad
        </Label>
        <Input
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="pz"
          className="h-10 text-sm"
          data-testid="custom-material-unit"
        />
      </div>
      <Button
        type="submit"
        className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700"
        data-testid="custom-material-add"
      >
        <Plus className="mr-1 h-4 w-4" /> Agregar
      </Button>
    </form>
  );
}

// ----------------------------------------------------------------------------
// EVA scale 0-10
// ----------------------------------------------------------------------------
function EvaScale({ value, onChange }) {
  return (
    <div data-testid="eva-scale">
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }, (_, i) => {
          const active = value === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(i)}
              className={`flex h-12 items-center justify-center rounded-md text-sm font-bold transition-all sm:h-14 ${
                active ? "text-white shadow-md ring-2 ring-offset-2 ring-slate-900 scale-105" : "text-slate-900"
              }`}
              style={{ backgroundColor: EVA_COLORS[i] }}
              data-testid={`eva-${i}`}
            >
              {i}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-center text-sm font-semibold text-slate-600">
        {EVA_TEXT[value || 0]}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Yes / No toggle (incidents)
// ----------------------------------------------------------------------------
function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      <button
        type="button"
        onClick={() => onChange(value === true ? null : true)}
        className={`min-h-[40px] min-w-[56px] rounded-lg border px-3 py-1.5 text-sm font-bold transition-all ${
          value === true
            ? "border-red-600 bg-red-600 text-white"
            : "border-slate-200 bg-white text-slate-600 hover:border-red-600 hover:text-red-600"
        }`}
        data-testid={`${testid}-yes`}
      >
        Si
      </button>
      <button
        type="button"
        onClick={() => onChange(value === false ? null : false)}
        className={`min-h-[40px] min-w-[56px] rounded-lg border px-3 py-1.5 text-sm font-bold transition-all ${
          value === false
            ? "border-emerald-600 bg-emerald-600 text-white"
            : "border-slate-200 bg-white text-slate-600 hover:border-emerald-600 hover:text-emerald-600"
        }`}
        data-testid={`${testid}-no`}
      >
        No
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Add intervention sheet
// ----------------------------------------------------------------------------
function AddInterventionSheet({ open, onOpenChange, nurses, onAdd, defaultStart }) {
  const [cat, setCat] = useState(Object.keys(CATS)[0]);
  const [item, setItem] = useState(null);
  const [start, setStart] = useState(defaultStart || "07:00");
  const [duration, setDuration] = useState(15);
  const [nurse, setNurse] = useState("__none__");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setItem(null);
      setStart(defaultStart || "07:00");
      setDuration(15);
      setNurse("__none__");
      setNotes("");
      setCat(Object.keys(CATS)[0]);
    }
  }, [open, defaultStart]);

  const pickItem = (it) => {
    setItem(it);
    setDuration(it.d);
  };

  const submit = () => {
    if (!item) {
      toast.error("Selecciona una intervencion");
      return;
    }
    onAdd({
      category: cat,
      name: item.n,
      start_time: start,
      end_time: addMinutes(start, duration),
      duration_minutes: Number(duration),
      nurse: nurse === "__none__" ? "" : nurse,
      notes: notes.trim(),
    });
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92vh]">
        <DrawerHeader className="border-b border-slate-200">
          <DrawerTitle className="text-lg font-bold">Agregar intervencion</DrawerTitle>
          <DrawerDescription className="sr-only">
            Elige una categoria, selecciona una intervencion y configura horario y enfermera
          </DrawerDescription>
        </DrawerHeader>

        <div className="overflow-y-auto px-4 pb-4 pt-3 sm:px-6">
          {/* Category chips */}
          <div className="mb-4 -mx-1 flex gap-2 overflow-x-auto pb-2 pl-1" data-testid="sheet-categories">
            {Object.entries(CATS).map(([name, c]) => {
              const active = cat === name;
              return (
                <button
                  key={name}
                  onClick={() => {
                    setCat(name);
                    setItem(null);
                  }}
                  className="min-h-[40px] flex-shrink-0 rounded-full px-3.5 py-2 text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: active ? c.color : c.bg,
                    color: active ? "#FFFFFF" : c.color,
                    border: `1.5px solid ${c.color}`,
                  }}
                  data-testid={`sheet-cat-${name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  {name}
                </button>
              );
            })}
          </div>

          {/* Items list */}
          {!item && (
            <div className="space-y-1.5">
              {CATS[cat].items.map((it) => (
                <button
                  key={it.n}
                  onClick={() => pickItem(it)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3 text-left transition-all hover:border-[#1D4ED8] hover:bg-blue-50"
                  data-testid={`sheet-item-${it.n.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  <span className="pr-2 text-sm font-semibold text-slate-800">{it.n}</span>
                  <span className="flex items-center gap-1 text-xs font-medium text-slate-500">
                    <Clock className="h-3.5 w-3.5" /> {it.d}m
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Configure */}
          {item && (
            <div className="space-y-4">
              <div
                className="rounded-xl p-3"
                style={{ backgroundColor: CATS[cat].bg, color: CATS[cat].color }}
              >
                <div className="text-xs font-bold uppercase tracking-wide opacity-80">{cat}</div>
                <div className="text-base font-bold">{item.n}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs font-semibold text-slate-600">Hora inicio</Label>
                  <Input
                    type="time"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    className="h-11 text-base"
                    data-testid="sheet-start-time"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs font-semibold text-slate-600">Duracion (min)</Label>
                  <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                    <SelectTrigger className="h-11 text-base" data-testid="sheet-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DUR_OPTS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {d} min
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">
                  Enfermera responsable (opcional)
                </Label>
                <Select value={nurse} onValueChange={setNurse}>
                  <SelectTrigger className="h-11 text-base" data-testid="sheet-nurse">
                    <SelectValue placeholder="- Sin asignar -" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">- Sin asignar -</SelectItem>
                    {nurses.filter(Boolean).map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">
                  Observaciones (opcional)
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej. revisar acceso IV antes de administrar"
                  rows={2}
                  className="text-base"
                  data-testid="sheet-notes"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="h-12 flex-1 rounded-xl"
                  onClick={() => setItem(null)}
                  data-testid="sheet-back-btn"
                >
                  Atras
                </Button>
                <Button
                  className="h-12 flex-1 rounded-xl bg-[#1D4ED8] text-base font-semibold hover:bg-[#1E40AF]"
                  onClick={submit}
                  data-testid="sheet-confirm-btn"
                >
                  <Plus className="mr-1.5 h-4 w-4" /> Agregar
                </Button>
              </div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ----------------------------------------------------------------------------
// Build share text
// ----------------------------------------------------------------------------
function buildShareText(p) {
  const s = p.shift || {};
  const c = p.closing || {};
  const interv = p.interventions || [];
  const materials = p.materials || [];
  const lines = [];
  lines.push("=== PROGRAMA DE CUIDADOS - CIUDAD ENFERMERIA ===");
  lines.push("");
  lines.push("== Datos del turno ==");
  lines.push(`Paciente: ${s.patient_name || "-"}`);
  const dur = computeShiftDuration(s);
  if (s.date === s.end_date || !s.end_date) {
    lines.push(
      `Fecha: ${s.date || "-"}  Inicio: ${s.start_time || "-"}  Termino: ${s.end_time || "-"}  Duracion: ${dur ? dur.label : "-"}`
    );
  } else {
    lines.push(
      `Inicio: ${s.date || "-"} ${s.start_time || "-"}  Termino: ${s.end_date || "-"} ${s.end_time || "-"}  Duracion: ${dur ? dur.label : "-"}`
    );
  }
  lines.push(`Enfermeras: ${(s.nurses || []).join(", ") || "-"}`);
  lines.push("");
  lines.push("== Programa de intervenciones ==");
  if (interv.length === 0) lines.push("(sin intervenciones)");
  interv
    .slice()
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
    .forEach((i, idx) => {
      lines.push(
        `${idx + 1}. ${i.start_time || "--:--"} (${i.duration_minutes}m) ${i.name} [${i.category}]`
      );
      if (i.nurse) lines.push(`   Enf: ${i.nurse}`);
      if (i.notes) lines.push(`   Notas: ${i.notes}`);
    });
  lines.push("");
  lines.push("== Consumo de materiales ==");
  if (materials.length === 0) {
    lines.push("(sin registro)");
  } else {
    const totalPieces = materials.reduce((sum, m) => sum + (m.quantity || 0), 0);
    materials.forEach((m) => {
      lines.push(`- ${m.name}: ${m.quantity} ${m.unit}`);
    });
    lines.push(`Total: ${totalPieces} piezas`);
  }
  lines.push("");
  lines.push("== Cierre de turno ==");
  lines.push(`Condicion: ${c.condition || "-"}  |  Emocional: ${c.emotion || "-"}`);
  lines.push(`Dolor (EVA): ${c.eva_pain ?? 0}/10`);
  const v = c.vitals || {};
  const readings = (v.readings || []).filter(
    (r) => r && (r.time || r.ta || r.fc || r.temp || r.spo2)
  );
  if (readings.length > 0) {
    readings.forEach((r, i) => {
      lines.push(
        `Toma ${i + 1}${r.time ? ` ${r.time}` : ""}: TA ${r.ta || "-"} | FC ${r.fc || "-"} | Temp ${r.temp || "-"} | SpO2 ${r.spo2 || "-"}`
      );
    });
  } else {
    lines.push(`Signos: TA ${v.ta || "-"} | FC ${v.fc || "-"} | Temp ${v.temp || "-"} | SpO2 ${v.spo2 || "-"}`);
  }
  if (v.weight_kg) lines.push(`Peso: ${v.weight_kg} kg`);

  const n = c.nutrition || {};
  const nbits = [];
  if (n.via) nbits.push(`Via: ${n.via}`);
  if (n.tolerance) nbits.push(`Tolerancia: ${n.tolerance}`);
  if (n.intake) nbits.push(`Consumo: ${n.intake}`);
  if (n.appetite) nbits.push(`Apetito: ${n.appetite}`);
  if (n.diet_type) nbits.push(`Dieta: ${n.diet_type}`);
  const mealBits = [];
  if (n.breakfast) mealBits.push(`Desayuno ${n.breakfast}`);
  if (n.lunch) mealBits.push(`Comida ${n.lunch}`);
  if (n.dinner) mealBits.push(`Cena ${n.dinner}`);
  if (mealBits.length) nbits.push(`Raciones: ${mealBits.join(" / ")}`);
  if (n.water_oral_ml) nbits.push(`Hidratacion oral: ${n.water_oral_ml} ml`);
  if (n.nausea === true) nbits.push("Nausea: Si");
  if (n.vomit === true) nbits.push("Vomito: Si");
  if (nbits.length) lines.push(`Alimentacion: ${nbits.join(" | ")}`);

  // Balance hidrico
  const wb = computeWaterBalance(c, s);
  if (wb) {
    const sign = wb.balance > 0 ? "+" : "";
    const parts = [`Ingresos ${wb.intake} ml`, `Egresos ${wb.output} ml`];
    const b = wb.breakdown;
    const breakdownParts = [];
    if (b.urine > 0) breakdownParts.push(`orina ${b.urine}`);
    if (b.vomit > 0) breakdownParts.push(`vomito ${b.vomit}`);
    if (b.drain > 0) breakdownParts.push(`drenaje ${b.drain}`);
    if (b.insensible > 0) breakdownParts.push(`insensible ${b.insensible}`);
    if (breakdownParts.length > 1) {
      parts.push(`(${breakdownParts.join(" + ")})`);
    }
    parts.push(`Balance ${sign}${wb.balance} ml`);
    lines.push(`Balance hidrico: ${parts.join(" | ")}`);
  }

  const el = c.elimination || {};
  const ubits = [];
  if (el.urine_method) ubits.push(`Metodo: ${el.urine_method}`);
  if (el.urine_appearance) ubits.push(`Tipo: ${el.urine_appearance}`);
  if (el.urine_volume) ubits.push(`Volumen: ${el.urine_volume}`);
  if (el.urine_times) ubits.push(`Veces: ${el.urine_times}`);
  if (el.urine_amount_ml) ubits.push(`Cantidad: ${el.urine_amount_ml} ml`);
  if (ubits.length) lines.push(`Miccion: ${ubits.join(" | ")}`);
  if (el.stool_present !== null && el.stool_present !== undefined) {
    if (el.stool_present === false) {
      lines.push("Evacuacion: No presento");
    } else {
      const sbits = [];
      if (el.stool_times) sbits.push(`Veces: ${el.stool_times}`);
      if (el.bristol) sbits.push(`Bristol: tipo ${el.bristol}`);
      if (el.stool_consistency) sbits.push(`Consistencia: ${el.stool_consistency}`);
      if (el.stool_appearance) sbits.push(`Aspecto: ${el.stool_appearance}`);
      if (el.stool_method) sbits.push(`Tipo: ${el.stool_method}`);
      lines.push(`Evacuacion: Si${sbits.length ? " | " + sbits.join(" | ") : ""}`);
    }
  }
  const incs = Object.entries(c.incidents || {})
    .filter(([_, v2]) => v2 === true)
    .map(([k]) => INC_LABELS[k]);
  lines.push(`Incidencias: ${incs.length ? incs.join(", ") : "ninguna"}`);
  if (c.incidents_desc) lines.push(`Descripcion: ${c.incidents_desc}`);

  // Sleep monitoring
  const sleepHrs = Array.isArray(c.sleep?.hours) ? [...c.sleep.hours].sort((a, b) => a - b) : [];
  if (sleepHrs.length || c.sleep?.observations) {
    const fmt = (h) => `${String(h).padStart(2, "0")}:00`;
    // Group contiguous ranges (e.g. [0,1,2,7,8] -> "00:00-03:00, 07:00-09:00")
    const ranges = [];
    let start = null, prev = null;
    for (const h of sleepHrs) {
      if (start === null) { start = h; prev = h; continue; }
      if (h === prev + 1) { prev = h; continue; }
      ranges.push([start, prev]);
      start = h; prev = h;
    }
    if (start !== null) ranges.push([start, prev]);
    const rangeStr = ranges
      .map(([a, b]) => `${fmt(a)}-${fmt((b + 1) % 24)}`)
      .join(", ");
    const bits = [`${sleepHrs.length} h / 24`];
    if (rangeStr) bits.push(rangeStr);
    if (c.sleep?.observations) bits.push(c.sleep.observations);
    lines.push(`Sueno: ${bits.join(" | ")}`);
  }

  // Medications administered
  const meds = Array.isArray(c.medications_given) ? c.medications_given : [];
  if (meds.length) {
    lines.push("");
    lines.push(`== Medicamentos administrados (${meds.length}) ==`);
    const sortedMeds = [...meds].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    for (const m of sortedMeds) {
      const parts = [
        m.time || "--:--",
        m.name || "(sin nombre)",
        m.dose,
        m.via,
        indicationShort(m.indication),
      ].filter(Boolean);
      lines.push(`- ${parts.join(" | ")}`);
      if (m.observations) lines.push(`    obs: ${m.observations}`);
    }
  }

  if ((c.pending || []).length) lines.push(`Pendientes: ${c.pending.join("; ")}`);
  if (c.observations) lines.push(`Observaciones: ${c.observations}`);
  lines.push(`Cierre: ${c.closing_nurse || "-"}  ${c.closing_time || ""}`);
  return lines.join("\n");
}

// ----------------------------------------------------------------------------
// Critical alert banner -> opens WhatsApp pre-filled to the supervisor
// ----------------------------------------------------------------------------
function SupervisorAlertBanner({ program, reasons }) {
  const [phone, setPhoneState] = useState(() => getSupervisorPhone());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(phone);
  const [sentAt, setSentAt] = useState(null);
  const patientName = (program?.shift?.patient_name || "").trim();
  const canAlert = patientName.length > 0;

  useEffect(() => {
    setDraft(phone);
  }, [phone]);

  const onSendAlert = () => {
    const msg = buildAlertMessage(program, reasons);
    const opened = openSupervisorWhatsApp(msg, phone);
    if (!opened) {
      toast.error("El navegador bloqueo la ventana. Permite ventanas emergentes e intenta de nuevo.");
      return;
    }
    if (!phone) {
      toast.info("Selecciona el contacto del supervisor en WhatsApp");
    } else {
      toast.success("WhatsApp abierto - presiona Enviar al supervisor");
    }
    setSentAt(new Date());
  };

  const onSavePhone = () => {
    if (!draft) {
      setSupervisorPhone("");
      setPhoneState("");
      setEditing(false);
      toast.success("Numero del supervisor eliminado");
      return;
    }
    if (!isValidPhone(draft)) {
      toast.error("Numero invalido. Usa formato internacional, ej. +5215512345678");
      return;
    }
    const normalized = normalizePhone(draft);
    setSupervisorPhone(normalized);
    setPhoneState(normalized);
    setEditing(false);
    toast.success("Numero del supervisor guardado en este dispositivo");
  };

  return (
    <div
      className="rounded-2xl border-2 border-[#DC2626] bg-gradient-to-br from-[#FEE2E2] to-[#FECACA] p-4 shadow-md sm:p-5"
      data-testid="critical-alert-banner"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#DC2626] text-white shadow">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-extrabold tracking-tight text-[#7F1D1D] sm:text-lg">
            Valores criticos detectados
          </h3>
          <p className="mt-0.5 text-xs text-[#991B1B] sm:text-sm">
            Avisa al supervisor por WhatsApp con un solo toque.
          </p>
          {reasons?.length > 0 && (
            <ul
              className="mt-2 list-disc space-y-0.5 pl-5 text-sm font-semibold text-[#7F1D1D]"
              data-testid="critical-alert-reasons"
            >
              {reasons.slice(0, 6).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
              {reasons.length > 6 && (
                <li className="font-normal italic">
                  +{reasons.length - 6} mas
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <Button
        onClick={onSendAlert}
        disabled={!canAlert}
        className="mt-4 h-14 w-full rounded-2xl bg-[#25D366] text-base font-bold text-white shadow-md hover:bg-[#1FB855] hover:shadow-lg disabled:cursor-not-allowed disabled:bg-slate-400 disabled:opacity-90"
        data-testid="supervisor-alert-btn"
      >
        <Bell className="mr-2 h-5 w-5" />
        {phone
          ? `Alertar al supervisor (${phone})`
          : "Alertar al supervisor por WhatsApp"}
      </Button>

      {!canAlert && (
        <p
          className="mt-2 text-center text-xs font-semibold text-[#991B1B]"
          data-testid="supervisor-alert-blocked"
        >
          Escribe el nombre del paciente (Seccion 1) para poder enviar la alerta.
        </p>
      )}

      {sentAt && (
        <p
          className="mt-2 text-center text-xs font-semibold text-[#15803D]"
          data-testid="supervisor-alert-sent"
        >
          WhatsApp abierto a las {sentAt.toLocaleTimeString("es-MX", {
            hour: "2-digit",
            minute: "2-digit",
          })}{" "}
          - confirma que el mensaje se envio
        </p>
      )}

      <div className="mt-3 border-t border-[#FCA5A5] pt-3">
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-[#7F1D1D] underline underline-offset-2 hover:text-[#450A0A]"
            data-testid="supervisor-phone-edit"
          >
            {phone
              ? "Cambiar numero del supervisor"
              : "Configurar numero del supervisor (opcional)"}
          </button>
        ) : (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-[#7F1D1D]">
              Numero del supervisor (formato internacional)
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="+5215512345678"
                inputMode="tel"
                className="h-11 flex-1 text-base"
                data-testid="supervisor-phone-input"
              />
              <Button
                onClick={onSavePhone}
                className="h-11 rounded-xl bg-[#1D4ED8] px-4 font-semibold hover:bg-[#1E40AF]"
                data-testid="supervisor-phone-save"
              >
                Guardar
              </Button>
              <Button
                onClick={() => {
                  setDraft(phone);
                  setEditing(false);
                }}
                variant="outline"
                className="h-11 rounded-xl px-4"
                data-testid="supervisor-phone-cancel"
              >
                Cancelar
              </Button>
            </div>
            <p className="text-[11px] text-[#7F1D1D]/80">
              Se guarda solo en este dispositivo. Incluye el codigo de pais (ej. +52 para Mexico).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
export default function CareProgramPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [program, setProgram] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [newNurse, setNewNurse] = useState("");
  const [newPending, setNewPending] = useState("");
  const [medCatalog, setMedCatalog] = useState([]);
  const [medHistory, setMedHistory] = useState([]);
  const [patientMeta, setPatientMeta] = useState(null);   // matched patient by name (for DPCA flag)
  const [clockTick, setClockTick] = useState(0);
  const autoSaveTimer = useRef(null);

  // Tick once a minute so critical-by-Rx-pending updates without requiring user input
  useEffect(() => {
    const id = setInterval(() => setClockTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Load
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/programs/${id}`);
        const rawShift = data.shift || {};
        // Migrate v1 programs (with duration_hours but no end_date/end_time)
        // by computing the end from start + duration_hours.
        const startDate = rawShift.date || new Date().toISOString().slice(0, 10);
        const startTime = rawShift.start_time || "07:00";
        let endDate = rawShift.end_date || "";
        let endTime = rawShift.end_time || "";
        if (!endDate || !endTime) {
          const hours = rawShift.duration_hours || 8;
          const migrated = addMinutesToShift(startDate, startTime, hours * 60);
          endDate = endDate || migrated.end_date;
          endTime = endTime || migrated.end_time;
        }
        setProgram({
          shift: {
            patient_name: "",
            date: startDate,
            start_time: startTime,
            end_date: endDate,
            end_time: endTime,
            duration_hours: 0,
            nurses: [],
            ...rawShift,
            end_date: endDate,
            end_time: endTime,
          },
          interventions: data.interventions || [],
          materials: data.materials || [],
          closing: {
            condition: "",
            emotion: "",
            eva_pain: 0,
            vitals: (() => {
              const raw = data.closing?.vitals || {};
              let readings = Array.isArray(raw.readings) ? [...raw.readings] : [];
              // Migrate legacy single-reading data into readings[0] if needed
              if (readings.length === 0 && (raw.ta || raw.fc || raw.temp || raw.spo2)) {
                readings.push({
                  time: "",
                  ta: raw.ta || "",
                  fc: raw.fc || "",
                  temp: raw.temp || "",
                  spo2: raw.spo2 || "",
                });
              }
              // Always have exactly 3 slots in the UI
              while (readings.length < 3) {
                readings.push({ time: "", ta: "", fc: "", temp: "", spo2: "" });
              }
              return {
                ta: "", fc: "", temp: "", spo2: "", weight_kg: "",
                ...raw,
                readings: readings.slice(0, 3),
              };
            })(),
            nutrition: {
              via: "", tolerance: "", intake: "", appetite: "", diet_type: "",
              breakfast: "", lunch: "", dinner: "",
              water_oral_ml: "",
              nausea: null, vomit: null,
              ...(data.closing?.nutrition || {}),
            },
            elimination: {
              urine_method: "", urine_appearance: "", urine_volume: "",
              urine_times: "", urine_amount_ml: "",
              stool_present: null, stool_consistency: "", stool_appearance: "", stool_method: "",
              stool_times: "", bristol: "",
              vomit_ml: "", drain_ml: "",
              ...(data.closing?.elimination || {}),
            },
            incidents: { caida: null, reaccion: null, dolor: null, svitales: null, agitacion: null, otra: null, ...(data.closing?.incidents || {}) },
            morse: {
              history_falling: null, secondary_dx: null, ambulatory_aid: null,
              iv_lock: null, gait: null, mental_status: null,
              ...(data.closing?.morse || {}),
            },
            sleep: {
              hours: [],
              observations: "",
              ...(data.closing?.sleep || {}),
            },
            medications_given: Array.isArray(data.closing?.medications_given)
              ? data.closing.medications_given
              : [],
            peritoneal_dialysis: {
              exchanges: [],
              morning_weight_kg: "",
              observations: "",
              ...(data.closing?.peritoneal_dialysis || {}),
            },
            gastrostomy: {
              feedings: [],
              tube_care: {},
              weight_kg: "",
              observations: "",
              ...(data.closing?.gastrostomy || {}),
            },
            incidents_desc: "",
            pending: [],
            observations: "",
            closing_nurse: "",
            closing_time: "",
            ...(data.closing || {}),
          },
          _meta: { id: data.id, owner_name: data.owner_name },
        });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar");
        nav("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const updateShift = (k, v) =>
    setProgram((p) => ({ ...p, shift: { ...p.shift, [k]: v } }));
  const updateClosing = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, [k]: v } }));
  const updateVitals = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, vitals: { ...p.closing.vitals, [k]: v } } }));
  const updateReading = (idx, k, v) =>
    setProgram((p) => {
      const readings = [...(p.closing.vitals.readings || [])];
      readings[idx] = { ...(readings[idx] || {}), [k]: v };
      return { ...p, closing: { ...p.closing, vitals: { ...p.closing.vitals, readings } } };
    });
  const updateNutrition = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, nutrition: { ...p.closing.nutrition, [k]: v } } }));
  const updateElimination = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, elimination: { ...p.closing.elimination, [k]: v } } }));
  const updateIncidents = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, incidents: { ...p.closing.incidents, [k]: v } } }));
  const updateMorse = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, morse: { ...p.closing.morse, [k]: v } } }));
  const updateSleep = (k, v) =>
    setProgram((p) => ({ ...p, closing: { ...p.closing, sleep: { ...p.closing.sleep, [k]: v } } }));
  const toggleSleepHour = (h) =>
    setProgram((p) => {
      const arr = Array.isArray(p.closing.sleep?.hours) ? p.closing.sleep.hours : [];
      const next = arr.includes(h) ? arr.filter((x) => x !== h) : [...arr, h].sort((a, b) => a - b);
      return { ...p, closing: { ...p.closing, sleep: { ...(p.closing.sleep || {}), hours: next } } };
    });

  const save = useCallback(
    async (silent = false) => {
      if (!program) return;
      setSaving(true);
      try {
        const payload = {
          shift: program.shift,
          interventions: program.interventions,
          materials: program.materials || [],
          closing: program.closing,
        };
        await api.put(`/programs/${id}`, payload);
        setSaveError(false);
        if (!silent) toast.success("Programa guardado");
      } catch (e) {
        setSaveError(true);
        if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
      } finally {
        setSaving(false);
      }
    },
    [program, id]
  );

  // Debounced auto-save
  useEffect(() => {
    if (!program || loading) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => save(true), 1200);
    return () => autoSaveTimer.current && clearTimeout(autoSaveTimer.current);
  }, [program, loading, save]);

  // Load authorized medications for the patient (debounced on name change)
  const patientNameForCatalog = (program?.shift?.patient_name || "").trim();
  const loadMedCatalog = useCallback(async () => {
    if (!patientNameForCatalog) {
      setMedCatalog([]);
      setMedHistory([]);
      setPatientMeta(null);
      return;
    }
    try {
      const [catRes, histRes, patRes] = await Promise.all([
        api.get("/patient-meds", { params: { patient_name: patientNameForCatalog } }),
        api.get("/patient-meds-history", { params: { patient_name: patientNameForCatalog } }),
        api.get("/patients"),
      ]);
      setMedCatalog(Array.isArray(catRes.data) ? catRes.data : []);
      setMedHistory(Array.isArray(histRes.data) ? histRes.data : []);
      const norm = patientNameForCatalog.toLowerCase();
      const match = (Array.isArray(patRes.data) ? patRes.data : []).find(
        (p) => (p.name || "").trim().toLowerCase() === norm
      );
      setPatientMeta(match || null);
    } catch (e) {
      // Silent: nurse can still proceed with already-given meds
      console.warn("med catalog/history fetch failed", e);
    }
  }, [patientNameForCatalog]);

  useEffect(() => {
    if (!patientNameForCatalog) {
      setMedCatalog([]);
      return;
    }
    const t = setTimeout(loadMedCatalog, 400);
    return () => clearTimeout(t);
  }, [patientNameForCatalog, loadMedCatalog]);

  const addNurse = () => {
    const n = newNurse.trim();
    if (!n) return;
    if (program.shift.nurses.includes(n)) {
      toast.warning("Esta enfermera ya esta agregada");
      return;
    }
    updateShift("nurses", [...program.shift.nurses, n]);
    setNewNurse("");
  };

  const removeNurse = (n) =>
    updateShift("nurses", program.shift.nurses.filter((x) => x !== n));

  const addPending = () => {
    const t = newPending.trim();
    if (!t) return;
    updateClosing("pending", [...program.closing.pending, t]);
    setNewPending("");
  };

  const removePending = (i) =>
    updateClosing("pending", program.closing.pending.filter((_, idx) => idx !== i));

  const addIntervention = (it) => {
    setProgram((p) => ({
      ...p,
      interventions: [...p.interventions, { id: crypto.randomUUID(), ...it }],
    }));
    toast.success("Intervencion agregada");
  };

  const removeIntervention = (iid) =>
    setProgram((p) => ({ ...p, interventions: p.interventions.filter((x) => x.id !== iid) }));

  // ---- Materials handlers ----
  const incrementMaterial = (name, unit) =>
    setProgram((p) => {
      const list = p.materials || [];
      const existing = list.find((m) => m.name === name);
      if (existing) {
        return {
          ...p,
          materials: list.map((m) =>
            m.id === existing.id ? { ...m, quantity: (m.quantity || 0) + 1 } : m
          ),
        };
      }
      return {
        ...p,
        materials: [...list, { id: crypto.randomUUID(), name, unit, quantity: 1 }],
      };
    });

  const decrementMaterial = (id) =>
    setProgram((p) => {
      const list = (p.materials || []).map((m) =>
        m.id === id ? { ...m, quantity: Math.max(0, (m.quantity || 0) - 1) } : m
      );
      return { ...p, materials: list.filter((m) => m.quantity > 0) };
    });

  const removeMaterial = (id) =>
    setProgram((p) => ({ ...p, materials: (p.materials || []).filter((m) => m.id !== id) }));

  const setMaterialQuantity = (id, q) =>
    setProgram((p) => ({
      ...p,
      materials: (p.materials || []).map((m) =>
        m.id === id ? { ...m, quantity: Math.max(0, Number(q) || 0) } : m
      ),
    }));

  const sortedInterv = useMemo(
    () =>
      [...(program?.interventions || [])].sort((a, b) =>
        (a.start_time || "").localeCompare(b.start_time || "")
      ),
    [program?.interventions]
  );

  const shareText = useMemo(() => (program ? buildShareText(program) : ""), [program]);

  const critical = useMemo(() => {
    if (!program) return { isCritical: false, reasons: [] };
    // clockTick included in deps so the banner refreshes every minute as
    // scheduled Rx doses pass their time.
    void clockTick;
    const base = evaluateCritical(program);
    // Augment with missed Rx scheduled doses
    const given = program.closing?.medications_given || [];
    const nowD = new Date();
    const nowMin = nowD.getHours() * 60 + nowD.getMinutes();
    const nowHM = `${String(nowD.getHours()).padStart(2, "0")}:${String(nowD.getMinutes()).padStart(2, "0")}`;
    const missed = pendingRxDoses(medCatalog, given, nowMin);
    const medReasons = missed.map(
      (m) =>
        `${m.med.name}${m.med.default_dose ? ` ${m.med.default_dose}` : ""} ${m.scheduled} no administrado a las ${nowHM} (retraso ${formatDelay(m.delayMinutes)})`
    );
    const reasons = [...(base.reasons || []), ...medReasons];
    return { isCritical: base.isCritical || medReasons.length > 0, reasons };
  }, [program, medCatalog, clockTick]);

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Programa de Cuidados", text: shareText });
      } catch (err) {
        // Share cancelled by user or not supported - non-fatal
        console.debug("Share cancelled or unsupported", err);
      }
    } else {
      setShareOpen(true);
    }
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Texto copiado");
    } catch (err) {
      console.debug("Clipboard API unavailable, falling back to dialog", err);
      setShareOpen(true);
    }
  };
  const onPrint = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Permite ventanas emergentes para imprimir");
    // Safe DOM construction (no document.write, no innerHTML, no string concat)
    const pre = w.document.createElement("pre");
    pre.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, monospace";
    pre.style.whiteSpace = "pre-wrap";
    pre.style.padding = "24px";
    pre.style.margin = "0";
    pre.textContent = shareText; // textContent is XSS-safe
    w.document.body.appendChild(pre);
    w.document.title = "Programa de Cuidados";
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  // Fetch evidences + image data URLs to embed in the PDF.
  // Videos can't render in PDF; we keep their metadata for a placeholder card.
  const fetchEvidencesForPDF = useCallback(async () => {
    const pid = program?._meta?.id;
    if (!pid) return [];
    try {
      const { data } = await api.get(`/programs/${pid}/evidence`);
      const list = Array.isArray(data) ? data : [];
      const out = await Promise.all(
        list.map(async (ev) => {
          if (ev.kind !== "image") return { ...ev, dataUrl: null };
          try {
            const r = await api.get(`/evidence/${ev.id}/file`, { responseType: "blob" });
            const dataUrl = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result);
              fr.onerror = reject;
              fr.readAsDataURL(r.data);
            });
            return { ...ev, dataUrl };
          } catch (e) {
            return { ...ev, dataUrl: null };
          }
        })
      );
      return out;
    } catch (e) {
      return [];
    }
  }, [program?._meta?.id]);

  const onDownloadPDF = async () => {
    try {
      const evidences = await fetchEvidencesForPDF();
      await exportProgramPDF(program, { evidences });
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
      console.error(e);
    }
  };

  const onShareWhatsApp = async () => {
    try {
      const evidences = await fetchEvidencesForPDF();
      const { blob, filename } = await exportProgramPDF(program, { returnBlob: true, evidences });
      const file = new File([blob], filename, { type: "application/pdf" });
      const patientName = program.shift?.patient_name?.trim() || "paciente";
      const shareData = {
        title: "Programa de Cuidados",
        text: `Programa de cuidados - ${patientName} (Ciudad Enfermeria)`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
        toast.success("PDF compartido");
        return;
      }
      // Fallback: download and open WhatsApp with text-only message
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      const waMsg = encodeURIComponent(
        `Adjunto el programa de cuidados de ${patientName} (PDF descargado: ${filename}).`
      );
      window.open(`https://wa.me/?text=${waMsg}`, "_blank", "noopener,noreferrer");
      toast.info("PDF descargado. WhatsApp se abre en otra pestana - adjunta el PDF manualmente.");
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled the share sheet
      console.error("WhatsApp share failed", e);
      toast.error("No se pudo compartir por WhatsApp");
    }
  };

  if (loading || !program) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <AppHeader subtitle="Editor de programa" />
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-24">
      <AppHeader subtitle="Editor de programa" />

      <main className="mx-auto max-w-3xl px-4 py-5 space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="h-10 px-2 text-slate-600 hover:bg-slate-100"
            onClick={() => nav("/dashboard")}
            data-testid="program-back-btn"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Volver
          </Button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {saving ? (
              <span className="inline-flex items-center gap-1" data-testid="autosave-saving">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando...
              </span>
            ) : saveError ? (
              <span className="inline-flex items-center gap-1 font-semibold text-red-600" data-testid="autosave-error">
                <AlertTriangle className="h-3.5 w-3.5" /> No guardado - revisa los campos
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="autosave-saved">
                <Check className="h-3.5 w-3.5" /> Guardado
              </span>
            )}
          </div>
        </div>

        {/* SECTION 1 - Shift data */}
        <section>
          <SecHdr num="1" title="Datos del turno" />
          <Card>
            <div className="space-y-4">
              <div>
                <Label className="mb-1 block text-sm font-semibold text-slate-700">
                  Nombre del paciente
                </Label>
                <Input
                  value={program.shift.patient_name}
                  readOnly
                  placeholder="(asignado al crear el programa)"
                  className="h-12 cursor-not-allowed bg-slate-100 text-base font-semibold text-slate-700"
                  data-testid="shift-patient"
                  title="El paciente se asigna al crear el programa y no puede cambiarse desde aqui."
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Para cambiar al paciente, crea un nuevo programa desde el panel.
                </p>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Inicio del turno
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={program.shift.date}
                    onChange={(e) => updateShift("date", e.target.value)}
                    className="h-12 text-base"
                    data-testid="shift-date"
                    aria-label="Fecha de inicio"
                  />
                  <Input
                    type="time"
                    value={program.shift.start_time}
                    onChange={(e) => updateShift("start_time", e.target.value)}
                    className="h-12 text-base"
                    data-testid="shift-start"
                    aria-label="Hora de inicio"
                  />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Termino del turno
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={program.shift.end_date}
                    onChange={(e) => updateShift("end_date", e.target.value)}
                    className="h-12 text-base"
                    data-testid="shift-end-date"
                    aria-label="Fecha de termino"
                  />
                  <Input
                    type="time"
                    value={program.shift.end_time}
                    onChange={(e) => updateShift("end_time", e.target.value)}
                    className="h-12 text-base"
                    data-testid="shift-end-time"
                    aria-label="Hora de termino"
                  />
                </div>
              </div>
              {(() => {
                const d = computeShiftDuration(program.shift);
                if (d) {
                  return (
                    <div
                      className="flex items-center justify-between rounded-xl bg-[#EFF6FF] px-4 py-3"
                      data-testid="shift-duration-display"
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-[#1E3A8A]">
                        <Clock className="h-4 w-4" />
                        Duracion calculada del turno
                      </div>
                      <div className="text-xl font-bold text-[#1D4ED8]" data-testid="shift-duration-value">
                        {d.label}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800"
                    data-testid="shift-duration-warning"
                  >
                    La hora de termino debe ser posterior a la de inicio.
                  </div>
                );
              })()}
            </div>
          </Card>

          <Card className="mt-3">
            <Label className="mb-2 block text-sm font-semibold text-slate-700">
              Enfermeras asignadas
            </Label>
            <div className="mb-3 flex flex-wrap gap-2" data-testid="nurses-list">
              {program.shift.nurses.length === 0 && (
                <span className="text-sm text-slate-400">Aun no hay enfermeras asignadas</span>
              )}
              {program.shift.nurses.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6FF] py-1.5 pl-3 pr-1.5 text-sm font-semibold text-[#1E3A8A]"
                  data-testid={`nurse-chip-${n.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <User2 className="h-3.5 w-3.5" />
                  {n}
                  <button
                    onClick={() => removeNurse(n)}
                    className="ml-1 rounded-full p-0.5 hover:bg-blue-200"
                    aria-label={`Remover ${n}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newNurse}
                onChange={(e) => setNewNurse(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNurse())}
                placeholder="Nombre de la enfermera"
                className="h-11"
                data-testid="nurse-input"
              />
              <Button
                onClick={addNurse}
                className="h-11 w-11 flex-shrink-0 rounded-xl bg-[#1D4ED8] p-0 hover:bg-[#1E40AF]"
                data-testid="nurse-add-btn"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </Card>
        </section>

        {/* SECTION 2 - Interventions */}
        <section>
          <SecHdr num="2" title="Programa de intervenciones" />
          <div className="space-y-2.5" data-testid="interventions-list">
            {sortedInterv.length === 0 && (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-5 py-8 text-center">
                <p className="text-sm font-semibold text-slate-600">Aun no hay intervenciones</p>
                <p className="mt-1 text-xs text-slate-400">Agrega la primera con el boton de abajo</p>
              </div>
            )}
            {sortedInterv.map((iv) => {
              const c = CATS[iv.category]?.color || "#1D4ED8";
              const bg = CATS[iv.category]?.bg || "#EFF6FF";
              return (
                <div
                  key={iv.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  data-testid={`intervention-${iv.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-md px-2 py-0.5 text-[11px] font-bold uppercase"
                          style={{ backgroundColor: bg, color: c }}
                        >
                          {iv.category}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-slate-500">
                          <Clock className="h-3 w-3" />
                          {iv.start_time || "--:--"} - {iv.end_time || addMinutes(iv.start_time, iv.duration_minutes)} ({iv.duration_minutes}m)
                        </span>
                      </div>
                      <div className="mt-1.5 text-sm font-bold text-slate-900 sm:text-base">{iv.name}</div>
                      {iv.nurse && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                          <User2 className="h-3 w-3" /> {iv.nurse}
                        </div>
                      )}
                      {iv.notes && (
                        <div className="mt-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
                          {iv.notes}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeIntervention(iv.id)}
                      className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                      data-testid={`intervention-remove-${iv.id}`}
                      aria-label="Eliminar intervencion"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            onClick={() => setSheetOpen(true)}
            className="mt-3 h-14 w-full rounded-2xl border-2 border-dashed border-[#1D4ED8] bg-[#EFF6FF] text-base font-bold text-[#1D4ED8] shadow-none hover:bg-[#DBEAFE]"
            data-testid="add-intervention-btn"
          >
            <Plus className="mr-2 h-5 w-5" /> Agregar intervencion
          </Button>
        </section>

        {/* SECTION 3 - Materials consumption */}
        <section>
          <SecHdr num="3" title="Consumo de materiales del turno" />
          <Card>
            <Label className="mb-2 block text-sm font-semibold text-slate-700">
              Toca un material para agregarlo. Toca de nuevo para sumar +1.
            </Label>
            <div className="mb-4 flex flex-wrap gap-1.5" data-testid="material-catalog">
              {MATERIAL_CATALOG.map((mat) => {
                const used = (program.materials || []).find((m) => m.name === mat.name);
                const qty = used?.quantity || 0;
                return (
                  <button
                    key={mat.name}
                    type="button"
                    onClick={() => incrementMaterial(mat.name, mat.unit)}
                    className={`relative inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${
                      qty > 0
                        ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-white text-slate-700 hover:border-emerald-600 hover:text-emerald-700"
                    }`}
                    data-testid={`material-catalog-${mat.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {mat.name}
                    {qty > 0 && (
                      <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[11px] font-bold text-white">
                        {qty}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <CustomMaterialAdder onAdd={incrementMaterial} />
          </Card>

          {(program.materials || []).length > 0 && (
            <Card className="mt-3">
              <div className="mb-2 flex items-center justify-between">
                <Label className="block text-sm font-semibold text-slate-700">
                  Materiales usados
                </Label>
                <span
                  className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800"
                  data-testid="materials-total-count"
                >
                  {program.materials.reduce((sum, m) => sum + (m.quantity || 0), 0)} piezas
                </span>
              </div>
              <div className="space-y-2" data-testid="materials-list">
                {program.materials.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50/40 px-3 py-2"
                    data-testid={`material-row-${m.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-slate-900">{m.name}</div>
                      <div className="text-[11px] text-slate-500">unidad: {m.unit}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => decrementMaterial(m.id)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-emerald-600 hover:text-emerald-700"
                        data-testid={`material-dec-${m.id}`}
                        aria-label="Disminuir"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <Input
                        type="number"
                        min="0"
                        value={m.quantity}
                        onChange={(e) => setMaterialQuantity(m.id, e.target.value)}
                        className="h-9 w-14 text-center text-base font-bold"
                        data-testid={`material-qty-${m.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => incrementMaterial(m.name, m.unit)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`material-inc-${m.id}`}
                        aria-label="Aumentar"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeMaterial(m.id)}
                        className="ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                        data-testid={`material-remove-${m.id}`}
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </section>

        {/* SECTION 4 - Closing notes */}
        <section>
          <SecHdr num="4" title="Notas de cierre del turno" />
          <div className="space-y-3">
            <Card>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Condicion general al cierre
              </Label>
              <Pills
                options={CONDITIONS}
                value={program.closing.condition}
                onChange={(v) => updateClosing("condition", v)}
                testid="closing-condition"
              />

              <Label className="mb-2 mt-4 block text-sm font-semibold text-slate-700">
                Estado emocional
              </Label>
              <Pills
                options={EMOTIONS}
                value={program.closing.emotion}
                onChange={(v) => updateClosing("emotion", v)}
                testid="closing-emotion"
              />

              <div className="mt-4">
                <Label className="mb-2 block text-sm font-semibold text-slate-700">
                  Nivel de dolor (EVA 0-10)
                </Label>
                <EvaScale
                  value={program.closing.eva_pain}
                  onChange={(v) => updateClosing("eva_pain", v)}
                />
              </div>
            </Card>

            <Card>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Signos vitales durante el turno
              </Label>
              <p className="mb-3 text-xs text-slate-500">
                Captura al menos 3 tomas. Indica la hora de cada una.
              </p>
              <div className="space-y-3" data-testid="vitals-readings">
                {[0, 1, 2].map((idx) => {
                  const r = program.closing.vitals.readings?.[idx] || {};
                  return (
                    <div
                      key={idx}
                      className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
                      data-testid={`vitals-reading-${idx}`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs font-bold uppercase tracking-wide text-[#1D4ED8]">
                          Toma {idx + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] font-semibold text-slate-500">Hora</Label>
                          <Input
                            type="time"
                            value={r.time || ""}
                            onChange={(e) => updateReading(idx, "time", e.target.value)}
                            className="h-9 w-28 text-sm"
                            data-testid={`vitals-time-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <VitalField
                          field="ta"
                          label="T/A"
                          value={r.ta}
                          onChange={(v) => updateReading(idx, "ta", v)}
                          placeholder="120/80"
                          testid={`vitals-ta-${idx}`}
                        />
                        <VitalField
                          field="fc"
                          label="FC (lpm)"
                          value={r.fc}
                          onChange={(v) => updateReading(idx, "fc", v)}
                          placeholder="72"
                          type="number"
                          testid={`vitals-fc-${idx}`}
                        />
                        <VitalField
                          field="temp"
                          label="Temp (C)"
                          value={r.temp}
                          onChange={(v) => updateReading(idx, "temp", v)}
                          placeholder="36.5"
                          type="number"
                          step="0.1"
                          testid={`vitals-temp-${idx}`}
                        />
                        <VitalField
                          field="spo2"
                          label="SpO2 (%)"
                          value={r.spo2}
                          onChange={(v) => updateReading(idx, "spo2", v)}
                          placeholder="96"
                          type="number"
                          min="0"
                          max="100"
                          testid={`vitals-spo2-${idx}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3">
                <Label className="mb-1 block text-xs font-semibold text-slate-500">
                  Peso del paciente (kg) <span className="font-normal text-slate-400">- para balance hidrico</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={program.closing.vitals.weight_kg}
                  onChange={(e) => updateVitals("weight_kg", e.target.value)}
                  placeholder="Ej. 70"
                  className="h-11 text-base"
                  data-testid="vitals-weight"
                />
              </div>
            </Card>

            <Card>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Alimentacion
              </Label>

              <SubLabel>Via de alimentacion</SubLabel>
              <Pills
                options={NUTRITION_VIA}
                value={program.closing.nutrition.via}
                onChange={(v) => updateNutrition("via", v)}
                testid="nutrition-via"
              />

              <SubLabel>Tolerancia</SubLabel>
              <Pills
                options={NUTRITION_TOLERANCE}
                value={program.closing.nutrition.tolerance}
                onChange={(v) => updateNutrition("tolerance", v)}
                testid="nutrition-tolerance"
              />

              <SubLabel>Consumo del platillo</SubLabel>
              <Pills
                options={NUTRITION_INTAKE}
                value={program.closing.nutrition.intake}
                onChange={(v) => updateNutrition("intake", v)}
                testid="nutrition-intake"
              />

              <SubLabel>Apetito</SubLabel>
              <Pills
                options={NUTRITION_APPETITE}
                value={program.closing.nutrition.appetite}
                onChange={(v) => updateNutrition("appetite", v)}
                testid="nutrition-appetite"
              />

              <SubLabel>Tipo de dieta</SubLabel>
              <Pills
                options={NUTRITION_DIET}
                value={program.closing.nutrition.diet_type}
                onChange={(v) => updateNutrition("diet_type", v)}
                testid="nutrition-diet"
              />

              <div className="mt-4 rounded-xl bg-orange-50/60 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-700">
                    Racion consumida por tiempo de comida
                  </div>
                  <div className="text-[10px] font-medium text-orange-700/80">
                    0 = no comio
                  </div>
                </div>
                <div className="mt-2 space-y-2.5">
                  {MEAL_TYPES.map((meal) => (
                    <div
                      key={meal.key}
                      className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3"
                    >
                      <div className="w-20 flex-shrink-0 text-sm font-semibold text-slate-700">
                        {meal.label}
                      </div>
                      <div className="flex flex-1 flex-wrap gap-1.5" data-testid={`meal-${meal.key}`}>
                        {MEAL_PORTIONS.map((p) => {
                          const active = program.closing.nutrition[meal.key] === p;
                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={() =>
                                updateNutrition(meal.key, active ? "" : p)
                              }
                              className={`min-h-[36px] min-w-[44px] rounded-lg border text-sm font-bold transition-all ${
                                active
                                  ? "border-orange-600 bg-orange-600 text-white shadow-sm"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-orange-500 hover:text-orange-700"
                              }`}
                              data-testid={`meal-${meal.key}-${p.replace("/", "-")}`}
                              aria-label={`${meal.label} ${p}`}
                            >
                              {p}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-col gap-1.5 border-t border-orange-200 pt-3 sm:flex-row sm:items-center sm:gap-3">
                  <Label
                    htmlFor="water-oral-ml"
                    className="w-full flex-shrink-0 text-xs font-bold uppercase tracking-wide text-orange-700 sm:w-40"
                  >
                    Hidratacion oral (ml)
                  </Label>
                  <Input
                    id="water-oral-ml"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder="Ej. 1200"
                    value={program.closing.nutrition.water_oral_ml}
                    onChange={(e) => updateNutrition("water_oral_ml", e.target.value)}
                    className="h-11 text-base sm:flex-1"
                    data-testid="water-oral-ml"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-2.5">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5">
                  <span className="text-sm font-medium text-slate-700">Presento nausea?</span>
                  <YesNo
                    value={program.closing.nutrition.nausea}
                    onChange={(v) => updateNutrition("nausea", v)}
                    testid="nutrition-nausea"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">Presento vomito?</span>
                  <YesNo
                    value={program.closing.nutrition.vomit}
                    onChange={(v) => updateNutrition("vomit", v)}
                    testid="nutrition-vomit"
                  />
                </div>
              </div>
            </Card>

            <Card>
              <Label className="mb-3 block text-sm font-semibold text-slate-700">
                Eliminacion
              </Label>

              {/* Live water-balance stat (ingresos vs egresos totales) */}
              {(() => {
                const wb = computeWaterBalance(program.closing, program.shift);
                if (!wb) {
                  return (
                    <div
                      className="mb-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500"
                      data-testid="water-balance-empty"
                    >
                      Captura <strong>hidratacion oral</strong>, <strong>cantidad de orina</strong> y{" "}
                      <strong>peso del paciente</strong> para calcular el balance hidrico completo.
                    </div>
                  );
                }
                const cfg = BALANCE_STATUS[wb.status];
                const sign = wb.balance > 0 ? "+" : "";
                const breakdownBits = [];
                if (wb.breakdown.urine > 0) breakdownBits.push(`Orina ${wb.breakdown.urine}`);
                if (wb.breakdown.vomit > 0) breakdownBits.push(`Vomito ${wb.breakdown.vomit}`);
                if (wb.breakdown.drain > 0) breakdownBits.push(`Drenaje ${wb.breakdown.drain}`);
                if (wb.breakdown.insensible > 0) breakdownBits.push(`Insensible ${wb.breakdown.insensible}`);
                return (
                  <div
                    className="mb-3 rounded-xl border p-3"
                    style={{ borderColor: cfg.dot, backgroundColor: cfg.bg }}
                    data-testid="water-balance"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold uppercase tracking-wide" style={{ color: cfg.color }}>
                        Balance hidrico
                      </div>
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold"
                        style={{ color: cfg.color }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cfg.dot }} />
                        {cfg.label}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Ingresos</div>
                        <div className="text-lg font-bold text-slate-900" data-testid="balance-intake">
                          {wb.intake} ml
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Egresos</div>
                        <div className="text-lg font-bold text-slate-900" data-testid="balance-output">
                          {wb.output} ml
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: cfg.color }}>
                          Balance
                        </div>
                        <div className="text-lg font-bold" style={{ color: cfg.color }} data-testid="balance-value">
                          {sign}
                          {wb.balance} ml
                        </div>
                      </div>
                    </div>
                    {breakdownBits.length > 1 && (
                      <div
                        className="mt-2 border-t border-white/60 pt-2 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                        data-testid="balance-breakdown"
                      >
                        Desglose egresos: {breakdownBits.join("  ·  ")}
                      </div>
                    )}
                    {wb.breakdown.insensible === 0 && !program.closing.vitals.weight_kg && (
                      <div className="mt-2 text-center text-[10px] font-medium text-slate-500">
                        Captura el <strong>peso (kg)</strong> en signos vitales para incluir perdida insensible.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="rounded-xl bg-blue-50/50 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#1D4ED8]">
                  Miccion / Diuresis
                </div>
                <SubLabel>Metodo</SubLabel>
                <Pills
                  options={URINE_METHOD}
                  value={program.closing.elimination.urine_method}
                  onChange={(v) => updateElimination("urine_method", v)}
                  testid="urine-method"
                />
                <SubLabel>Aspecto</SubLabel>
                <Pills
                  options={URINE_APPEARANCE}
                  value={program.closing.elimination.urine_appearance}
                  onChange={(v) => updateElimination("urine_appearance", v)}
                  testid="urine-appearance"
                />
                <SubLabel>Volumen</SubLabel>
                <Pills
                  options={URINE_VOLUME}
                  value={program.closing.elimination.urine_volume}
                  onChange={(v) => updateElimination("urine_volume", v)}
                  testid="urine-volume"
                />
                <SubLabel>Veces que orino</SubLabel>
                <Pills
                  options={URINE_TIMES}
                  value={program.closing.elimination.urine_times}
                  onChange={(v) => updateElimination("urine_times", v)}
                  testid="urine-times"
                />
                <SubLabel>Cantidad aproximada (ml)</SubLabel>
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  placeholder="Ej. 800"
                  value={program.closing.elimination.urine_amount_ml}
                  onChange={(e) => updateElimination("urine_amount_ml", e.target.value)}
                  className="h-11 text-base"
                  data-testid="urine-amount-ml"
                />
              </div>

              <div className="mt-3 rounded-xl bg-amber-50/50 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-amber-700">
                    Evacuacion
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600">Presento?</span>
                    <YesNo
                      value={program.closing.elimination.stool_present}
                      onChange={(v) => updateElimination("stool_present", v)}
                      testid="stool-present"
                    />
                  </div>
                </div>

                {program.closing.elimination.stool_present === true && (
                  <>
                    <SubLabel>Consistencia</SubLabel>
                    <Pills
                      options={STOOL_CONSISTENCY}
                      value={program.closing.elimination.stool_consistency}
                      onChange={(v) => updateElimination("stool_consistency", v)}
                      testid="stool-consistency"
                    />
                    <SubLabel>Aspecto</SubLabel>
                    <Pills
                      options={STOOL_APPEARANCE}
                      value={program.closing.elimination.stool_appearance}
                      onChange={(v) => updateElimination("stool_appearance", v)}
                      testid="stool-appearance"
                    />
                    <SubLabel>Tipo</SubLabel>
                    <Pills
                      options={STOOL_METHOD}
                      value={program.closing.elimination.stool_method}
                      onChange={(v) => updateElimination("stool_method", v)}
                      testid="stool-method"
                    />
                    <SubLabel>Veces que evacuo</SubLabel>
                    <Pills
                      options={STOOL_TIMES}
                      value={program.closing.elimination.stool_times}
                      onChange={(v) => updateElimination("stool_times", v)}
                      testid="stool-times"
                    />

                    <SubLabel>Escala de Bristol</SubLabel>
                    <div className="grid grid-cols-7 gap-1.5" data-testid="bristol-scale">
                      {BRISTOL_SCALE.map((b) => {
                        const active = program.closing.elimination.bristol === b.type;
                        return (
                          <button
                            key={b.type}
                            type="button"
                            onClick={() =>
                              updateElimination("bristol", active ? "" : b.type)
                            }
                            className={`flex h-12 flex-col items-center justify-center rounded-lg border-2 text-base font-bold transition-all ${
                              active
                                ? "text-white shadow-md ring-2 ring-offset-1 ring-slate-900 scale-105"
                                : "bg-white text-slate-700 hover:scale-105"
                            }`}
                            style={{
                              borderColor: b.color,
                              backgroundColor: active ? b.color : undefined,
                            }}
                            data-testid={`bristol-${b.type}`}
                            title={b.desc}
                            aria-label={`Bristol tipo ${b.type}: ${b.short}`}
                          >
                            {b.type}
                          </button>
                        );
                      })}
                    </div>
                    {program.closing.elimination.bristol && (
                      <div
                        className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs"
                        data-testid="bristol-description"
                      >
                        <span className="font-bold text-slate-700">
                          Tipo {program.closing.elimination.bristol}:
                        </span>{" "}
                        <span className="text-slate-600">
                          {BRISTOL_SCALE.find((b) => b.type === program.closing.elimination.bristol)?.desc}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-3 rounded-xl bg-slate-50 p-3" data-testid="other-egresos">
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                  Otros egresos (para balance hidrico)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block text-xs font-semibold text-slate-500">
                      Vomito (ml)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      value={program.closing.elimination.vomit_ml}
                      onChange={(e) => updateElimination("vomit_ml", e.target.value)}
                      className="h-11 text-base"
                      data-testid="vomit-ml"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs font-semibold text-slate-500">
                      Drenajes (ml)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      value={program.closing.elimination.drain_ml}
                      onChange={(e) => updateElimination("drain_ml", e.target.value)}
                      className="h-11 text-base"
                      data-testid="drain-ml"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <Label className="mb-3 block text-sm font-semibold text-slate-700">
                Incidencias durante el turno
              </Label>
              <div className="space-y-2.5">
                {Object.entries(INC_QUESTIONS).map(([k, q]) => (
                  <div key={k} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5 last:border-0">
                    <span className="text-sm font-medium text-slate-700">{q}</span>
                    <YesNo
                      value={program.closing.incidents[k]}
                      onChange={(v) => updateIncidents(k, v)}
                      testid={`incident-${k}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Label className="mb-1 block text-xs font-semibold text-slate-500">
                  Descripcion de incidencias (si aplica)
                </Label>
                <Textarea
                  value={program.closing.incidents_desc}
                  onChange={(e) => updateClosing("incidents_desc", e.target.value)}
                  placeholder="Que ocurrio, hora y acciones tomadas..."
                  rows={2}
                  className="text-base"
                  data-testid="incidents-desc"
                />
              </div>
            </Card>

            <Card>
              <div className="mb-2 flex items-center justify-between gap-2">
                <Label className="block text-sm font-semibold text-slate-700">
                  Monitoreo de sueno <span className="font-normal text-slate-500">(marca las horas que el paciente durmio)</span>
                </Label>
                {(() => {
                  const hrs = Array.isArray(program.closing.sleep?.hours) ? program.closing.sleep.hours : [];
                  const total = hrs.length;
                  let color = "#6B7280", bg = "#F1F5F9", dot = "#94A3B8";
                  if (total >= 6 && total <= 9) { color = "#15803D"; bg = "#DCFCE7"; dot = "#16A34A"; }
                  else if (total > 0 && (total < 6 || total > 9)) { color = "#A16207"; bg = "#FEF9C3"; dot = "#CA8A04"; }
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold"
                      style={{ backgroundColor: bg, color }}
                      data-testid="sleep-badge"
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dot }} />
                      {total} h / 24
                    </span>
                  );
                })()}
              </div>
              <div
                className="mt-2 grid grid-cols-6 gap-1.5 sm:grid-cols-12"
                data-testid="sleep-hours-grid"
                role="group"
                aria-label="Selector de horas dormidas"
              >
                {Array.from({ length: 24 }, (_, h) => {
                  const hrs = Array.isArray(program.closing.sleep?.hours) ? program.closing.sleep.hours : [];
                  const active = hrs.includes(h);
                  // Visual hint for shift periods (madrugada/dia/tarde/noche)
                  const period =
                    h < 6 ? "night" : h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
                  const idleTone =
                    period === "night"
                      ? "bg-slate-50 text-slate-500 hover:border-[#312E81]"
                      : period === "morning"
                      ? "bg-amber-50 text-amber-700 hover:border-amber-500"
                      : period === "afternoon"
                      ? "bg-sky-50 text-sky-700 hover:border-sky-500"
                      : "bg-indigo-50 text-indigo-700 hover:border-indigo-500";
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => toggleSleepHour(h)}
                      aria-pressed={active}
                      className={`flex h-12 items-center justify-center rounded-lg border text-sm font-bold tabular-nums transition-all ${
                        active
                          ? "border-[#1E3A8A] bg-[#1D4ED8] text-white shadow-sm"
                          : `border-slate-200 ${idleTone}`
                      }`}
                      data-testid={`sleep-hour-${h}`}
                      title={`${String(h).padStart(2, "0")}:00 a ${String((h + 1) % 24).padStart(2, "0")}:00`}
                    >
                      {String(h).padStart(2, "0")}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-slate-200" /> Madrugada 00-05
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-amber-200" /> Manana 06-11
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-sky-200" /> Tarde 12-17
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded bg-indigo-200" /> Noche 18-23
                </span>
              </div>
              <div className="mt-3">
                <Label className="mb-1 block text-xs font-semibold text-slate-600">
                  Observaciones del sueno
                </Label>
                <Textarea
                  value={program.closing.sleep?.observations || ""}
                  onChange={(e) => updateSleep("observations", e.target.value)}
                  placeholder="Ej. Sueno interrumpido, ronquidos, despertares por dolor, uso de medicamento para dormir..."
                  rows={2}
                  className="text-base"
                  data-testid="sleep-observations"
                />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Referencia adulto mayor: <strong>6-9 h</strong> de sueno por dia. Fuera de ese rango se marca en amarillo.
              </p>
            </Card>

            <Card>
              <div className="mb-2 flex items-center justify-between gap-2">
                <Label className="block text-sm font-semibold text-slate-700">
                  Medicamentos administrados <span className="font-normal text-slate-500">(catalogo autorizado por paciente)</span>
                </Label>
                <span
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700"
                  data-testid="med-given-count"
                >
                  {(program.closing.medications_given || []).length}
                </span>
              </div>
              <MedicationAdminLog
                catalog={medCatalog}
                given={program.closing.medications_given || []}
                history={medHistory}
                onChange={(next) =>
                  setProgram((p) => ({
                    ...p,
                    closing: { ...p.closing, medications_given: next },
                  }))
                }
                nurseName={user?.name}
                patientName={program.shift.patient_name}
              />
            </Card>

            {patientMeta?.peritoneal_dialysis_active && (
              <Card>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="block text-sm font-semibold text-slate-700">
                    Dialisis Peritoneal (DPCA) <span className="font-normal text-slate-500">(bitacora del turno)</span>
                  </Label>
                  <span
                    className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-800"
                    data-testid="dialysis-count-badge"
                  >
                    {(program.closing.peritoneal_dialysis?.exchanges || []).length} recambios
                  </span>
                </div>
                <PeritonealDialysisSection
                  pd={program.closing.peritoneal_dialysis}
                  onChange={(next) =>
                    setProgram((p) => ({
                      ...p,
                      closing: { ...p.closing, peritoneal_dialysis: next },
                    }))
                  }
                  patientName={program.shift.patient_name}
                  patientAddress={[patientMeta?.room, patientMeta?.bed && `#${patientMeta.bed}`].filter(Boolean).join(" ")}
                  nurseName={user?.name}
                />
              </Card>
            )}

            {patientMeta?.gastrostomy_active && (
              <Card>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="block text-sm font-semibold text-slate-700">
                    Gastrostomia (PEG) <span className="font-normal text-slate-500">(alimentacion por sonda)</span>
                  </Label>
                  <span
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
                    data-testid="peg-count-badge"
                  >
                    {(program.closing.gastrostomy?.feedings || []).length} tomas
                  </span>
                </div>
                <GastrostomySection
                  g={program.closing.gastrostomy}
                  onChange={(next) =>
                    setProgram((p) => ({
                      ...p,
                      closing: { ...p.closing, gastrostomy: next },
                    }))
                  }
                  patientName={program.shift.patient_name}
                  patientAddress={[patientMeta?.room, patientMeta?.bed && `#${patientMeta.bed}`].filter(Boolean).join(" ")}
                  nurseName={user?.name}
                />
              </Card>
            )}

            <Card>
              <div className="mb-2 flex items-center justify-between gap-2">
                <Label className="block text-sm font-semibold text-slate-700">
                  Escala de Morse <span className="font-normal text-slate-500">(riesgo de caidas)</span>
                </Label>
                {(() => {
                  if (!isMorseFilled(program.closing.morse)) return null;
                  const total = computeMorseTotal(program.closing.morse);
                  const lvl = morseLevel(total);
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold"
                      style={{ backgroundColor: lvl.bg, color: lvl.color }}
                      data-testid="morse-badge"
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: lvl.dot }} />
                      {total} - {lvl.label}
                    </span>
                  );
                })()}
              </div>
              <div className="space-y-3" data-testid="morse-questions">
                {MORSE_QUESTIONS.map((q) => (
                  <div key={q.key} className="rounded-xl bg-slate-50 p-3">
                    <div className="mb-1.5 text-sm font-semibold text-slate-700">{q.title}</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`morse-${q.key}`}>
                      {q.opts.map((o) => {
                        const active = program.closing.morse[q.key] === o.score;
                        return (
                          <button
                            key={o.score}
                            type="button"
                            onClick={() =>
                              updateMorse(q.key, active ? null : o.score)
                            }
                            className={`flex items-start gap-1.5 rounded-lg border px-3 py-1.5 text-left text-xs font-semibold transition-all ${
                              active
                                ? "border-[#1D4ED8] bg-[#1D4ED8] text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-700 hover:border-[#1D4ED8]"
                            }`}
                            data-testid={`morse-${q.key}-${o.score}`}
                          >
                            <span className={`flex h-5 min-w-5 items-center justify-center rounded px-1 text-[10px] font-bold ${active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
                              {o.score}
                            </span>
                            <span className="leading-tight">{o.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                Interpretacion: <strong>0-24</strong> bajo, <strong>25-44</strong> medio, <strong>&ge;45</strong> alto.
              </p>
            </Card>

            <Card>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Pendientes para el siguiente turno
              </Label>
              <div className="mb-2 space-y-1.5" data-testid="pending-list">
                {program.closing.pending.length === 0 && (
                  <span className="text-sm text-slate-400">Aun no hay pendientes</span>
                )}
                {program.closing.pending.map((t, i) => (
                  <div
                    key={`${t}-${i}`}
                    className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                  >
                    <span className="text-sm text-slate-700">{t}</span>
                    <button
                      onClick={() => removePending(i)}
                      className="rounded p-1 text-red-500 hover:bg-red-50"
                      aria-label="Remover pendiente"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newPending}
                  onChange={(e) => setNewPending(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPending())}
                  placeholder="Ej. revisar herida a las 14:00"
                  className="h-11"
                  data-testid="pending-input"
                />
                <Button
                  onClick={addPending}
                  className="h-11 w-11 flex-shrink-0 rounded-xl bg-[#1D4ED8] p-0 hover:bg-[#1E40AF]"
                  data-testid="pending-add-btn"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            </Card>

            <Card>
              <Label className="mb-2 block text-sm font-semibold text-slate-700">
                Observaciones y firma de cierre
              </Label>
              <Textarea
                value={program.closing.observations}
                onChange={(e) => updateClosing("observations", e.target.value)}
                placeholder="Comportamiento del paciente, cambios observados, comunicacion con la familia..."
                rows={3}
                className="text-base"
                data-testid="closing-observations"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs font-semibold text-slate-500">
                    Enfermera que cierra
                  </Label>
                  <Select
                    value={program.closing.closing_nurse || "__none__"}
                    onValueChange={(v) => updateClosing("closing_nurse", v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger className="h-11 text-base" data-testid="closing-nurse">
                      <SelectValue placeholder="- Seleccionar -" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">- Seleccionar -</SelectItem>
                      {program.shift.nurses.map((n) => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-xs font-semibold text-slate-500">
                    Hora real de cierre
                  </Label>
                  <Input
                    type="time"
                    value={program.closing.closing_time}
                    onChange={(e) => updateClosing("closing_time", e.target.value)}
                    className="h-11 text-base"
                    data-testid="closing-time"
                  />
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* SECTION 4.5 - Evidences (photos / videos) */}
        <section>
          <SecHdr num="5" title="Evidencias del turno" />
          <Card>
            <EvidenceGallery
              programId={program?._meta?.id}
              canDelete={user?.role === "admin"}
            />
          </Card>
        </section>

        {/* SECTION 5 - Share */}
        <section>
          {critical.isCritical && (
            <div className="mb-4">
              <SupervisorAlertBanner
                program={program}
                reasons={critical.reasons}
              />
            </div>
          )}
          <SecHdr num="6" title="Compartir programa" />
          <Card>
            <div className="mb-3">
              <p className="text-sm font-semibold text-slate-700">Descargar como documento</p>
              <p className="mt-0.5 text-xs text-slate-500">
                PDF con plantilla institucional Ciudad Enfermeria — apto para archivar o enviar a la familia
              </p>
            </div>
            <Button
              onClick={onDownloadPDF}
              className="h-14 w-full rounded-2xl bg-gradient-to-r from-[#1E3A8A] to-[#1D4ED8] text-base font-bold shadow-md hover:shadow-lg"
              data-testid="pdf-btn"
            >
              <FileDown className="mr-2 h-5 w-5" /> Descargar PDF institucional
            </Button>

            <Button
              onClick={onShareWhatsApp}
              className="mt-2 h-14 w-full rounded-2xl bg-[#25D366] text-base font-bold text-white shadow-md hover:bg-[#1FB855] hover:shadow-lg"
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="mr-2 h-5 w-5" /> Compartir por WhatsApp
            </Button>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Button
                onClick={onShare}
                variant="outline"
                className="h-12 rounded-xl border-[#1D4ED8] text-sm font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]"
                data-testid="share-btn"
              >
                <Share2 className="mr-2 h-4 w-4" /> Compartir
              </Button>
              <Button
                onClick={onCopy}
                variant="outline"
                className="h-12 rounded-xl border-[#1D4ED8] text-sm font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]"
                data-testid="copy-btn"
              >
                <Copy className="mr-2 h-4 w-4" /> Copiar texto
              </Button>
              <Button
                onClick={onPrint}
                variant="outline"
                className="h-12 rounded-xl border-[#1D4ED8] text-sm font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]"
                data-testid="print-btn"
              >
                <Printer className="mr-2 h-4 w-4" /> Imprimir
              </Button>
            </div>
          </Card>
        </section>

        <Button
          onClick={() => save(false)}
          disabled={saving}
          className="h-14 w-full rounded-2xl bg-emerald-600 text-base font-bold shadow-md hover:bg-emerald-700"
          data-testid="save-btn"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Save className="mr-2 h-5 w-5" /> Guardar programa</>)}
        </Button>
      </main>

      <AddInterventionSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        nurses={program.shift.nurses}
        onAdd={addIntervention}
        defaultStart={program.shift.start_time}
      />

      {/* Fallback copy dialog */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-bold">Selecciona y copia el texto</h3>
              <button onClick={() => setShareOpen(false)} className="rounded p-1 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Textarea
              readOnly
              value={shareText}
              rows={12}
              className="font-mono text-xs"
              data-testid="share-textarea"
            />
          </div>
        </div>
      )}
    </div>
  );
}
